import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { Cms, PAGE_TYPES, TempAny } from '@impact/data';

import { chevronRight } from '../../../icons';
import { NavigationService } from '../navigation.service';
import { megaNavigationColumnsMaxSize } from './mega-navigation.component';
import { ArticleFilterTypes } from '../../pages/articles-overview-page/articles-overview-page.component';

export enum ArticlesNavigationCategory {
    CATEGORIES = 'articleCategories',
    MAKES = 'articleMakes',
}
@Component({
    selector: 'app-mega-navigation-articles-universe',
    template: `
        <div class="mega-navigation-layout-wrapper">
            <div class="mega-navigation-layout-wrapper-inner">
                <div class="mega-navigation__content-header" [ngClass]="{'is-ready': isReady}">
                    <h2 class="mega-navigation-spot-layout__content-header-text">{{ data?.name }}</h2>
                    <a [routerLink]="articlesOverviewPageUrl" class="button--primary">
                        <span>{{ 'articles.show_all_articles_link_text' | translate }}</span>
                    </a>
                </div>

                <div class="mega-navigation-content-layout" [ngClass]="{'is-ready': isReady}">
                    <div class="mega-navigation__content">
                        <!-- LEFT COLUMN -->
                        <div class="mega-navigation__content-column-left">
                            <ul class="mega-navigation__navigation-list">
                                <li class="mega-navigation__navigation-list-item" *ngIf="data?.articlesNavigationData?.articleCategoriesTagNavigationHeader">
                                    <ng-container *ngTemplateOutlet="Level1Button; context: {
                                        text: data?.articlesNavigationData?.articleCategoriesTagNavigationHeader,
                                        category: articlesNavigationCategory.CATEGORIES
                                    }"></ng-container>
                                </li>
                                <li class="mega-navigation__navigation-list-item" *ngIf="data?.articlesNavigationData?.vehicleMakesTagsHeadline">
                                    <ng-container *ngTemplateOutlet="Level1Button; context: {
                                        text: data?.articlesNavigationData?.vehicleMakesTagsHeadline,
                                        category: articlesNavigationCategory.MAKES
                                    }"></ng-container>
                                </li>
                            </ul>
                        </div>

                        <!-- RIGHT COLUMN -->
                        <div class="mega-navigation__content-column-right">
                            <!-- Categories -->
                            <ng-container *ngIf="activeNavigationCategory === articlesNavigationCategory.CATEGORIES">
                                <ng-container *ngTemplateOutlet="CategoryLinks; context: { items: articlesCategoriesTagsList }"></ng-container>
                            </ng-container>

                            <!-- Brands -->
                            <ng-container *ngIf="activeNavigationCategory === articlesNavigationCategory.MAKES">
                                <ng-container *ngTemplateOutlet="CategoryLinks; context: { items: articlesMakesTagsList}"></ng-container>
                            </ng-container>
                        </div>
                    </div>
                </div>
            </div>

            <!-- MEGA NAVIGATION SPOT -->
            <div
                class="mega-navigation__spot-wrapper"
                *ngIf="navigationSpot | isValidNavigationSpot"
            >
                <app-mega-navigation-spot
                    [data]="navigationSpot"
                ></app-mega-navigation-spot>
            </div>
        </div>

        <!-- TEMPLATES -->
        <ng-template #Level1Button let-text="text" let-category="category">
            <button type="button" class="mega-navigation__navigation-list-link"
                [ngClass]="{'is-active': activeNavigationCategory === category}"
                (click)="setActiveCategory(category)"
                (mouseover)="setActiveCategory(category)">
                {{text}}
            </button>
            <div class="mega-navigation__navigation-active-indicator">${chevronRight}</div>
        </ng-template>

        <ng-template #CategoryHeader let-text="text" let-buttonText="buttonText">
            <div class="mega-navigation-spot-layout__content-header">
                <h2 *ngIf="text"
                    class="mega-navigation-spot-layout__content-header-text">
                    {{text}}
                </h2>
                <a [routerLink]="articlesOverviewPageUrl" class="button--primary">
                    {{buttonText}}
                </a>
            </div>
        </ng-template>

        <ng-template #CategoryLinks let-items="items">
            <ul class="mega-navigation-spot-layout__content-list"
                [ngClass]="{
                    'double-column': items.length > columnItemsSize,
                    'triple-column': items.length > columnItemsSize * 2
                }">
                <li *ngFor="let tag of items">
                    <a [routerLink]="articlesOverviewPageUrl" [queryParams]="tag?.param">{{tag?.displayName}}</a>
                </li>
            </ul>
        </ng-template>
    `,
    changeDetection: ChangeDetectionStrategy.OnPush
})

export class MegaNavigationArticlesUniverseComponent implements OnChanges {
    @Input() data: Cms.NavigationDto;

    navigationSpot: Cms.MegaNavigationSpotDto;
    pageTypes = PAGE_TYPES;
    articlesOverviewPageUrl: string;
    articlesCategoriesTagsList: any[];
    articlesMakesTagsList: any[];
    articlesNavigationCategory = ArticlesNavigationCategory;

    activeNavigationCategory: string;
    columnItemsSize = megaNavigationColumnsMaxSize;
    isReady: boolean;

    constructor(
        private cdr: ChangeDetectorRef,
        public navigationService: NavigationService
    ) {
        this.setActiveCategory(ArticlesNavigationCategory.CATEGORIES);
    }

    ngOnChanges(changes: SimpleChanges): void {
        const pageData = changes.data?.currentValue;
        this.articlesOverviewPageUrl = this.data.url;
        this.navigationSpot = changes.data.currentValue.megaNavigationSpot;

        this.data = pageData;

        this.articlesCategoriesTagsList = this.transformArticleTags(pageData.articlesNavigationData.articleCategoriesTagNavigation, ArticleFilterTypes.CATEGORY);
        this.articlesMakesTagsList = this.transformArticleTags(pageData.articlesNavigationData.vehicleMakesTagsNavigation, ArticleFilterTypes.MAKE);

        this.isReady = false;
        this.cdr.markForCheck();

        setTimeout(() => {
            this.isReady = true;
            this.cdr.markForCheck();
        }, 350);

        this.cdr.markForCheck();
    }

    transformArticleTags(tags: TempAny, category: string) {
        const formattedTags = tags.map((tag: TempAny) => {
            const cleanName = tag.displayName.normalize("NFD").replace(/[\u0300-\u036f]/g, ""); // Normalizes and removes accents from string
            const key = cleanName.replace(/[^a-zA-Z0-9\s]/g, '').replaceAll(' ', '-').toLowerCase(); // Removes special characters, converts spaces to dashes, and sets string to lowercases

            return {
                displayName: tag.displayName,
                id: tag.id,
                url: `${this.articlesOverviewPageUrl}`,
                key: key,
                param: {
                    [category]: key
                }
            };
        });

        return formattedTags;
    }

    setActiveCategory(category: string) {
        this.activeNavigationCategory = category;
    }
}
